<template>
  <LcTabs v-bind="{ tabs }" adaptive-height>
    <template #tab="{ tab }">
      <div class="flex flex-col items-center justify-center text-center">
        <span :class="{ 'text-xs': !!tab.data.tierMultiplier }">
          {{
            tab.name === DEFAULT_TAB_NAME
              ? $t(`info.prizes.tiertab.default.${lotteryType}`)
              : getTranslatedAddOnTitle(tab.data.addOnName)
          }}
        </span>

        <span v-if="tab.data.tierMultiplier">
          {{ $t('tiertable.multiplier', { value: tab.data.tierMultiplier }) }}
        </span>
      </div>
    </template>

    <template #tab-panel="{ tab }">
      <TierTable
        v-if="tab?.data?.prizeTiers"
        v-bind="{ addOns, licensedTerritory, lotteryId }"
        :class="tabs.length > 1 ? '-mt-050' : '-mt-150'"
        :grouped-prize-tiers="getGroupedPrizeTiers(tab.data.prizeTiers)"
        type="prizes"
      />
    </template>
  </LcTabs>
</template>

<script lang="ts" setup>
import type { PrizeTier } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { TierTablePrizeTier, TierTableProps } from './TierTable.vue'

export type PrizeTierTab = {
  data: {
    addOnName?: Maybe<string>
    prizeTiers: Omit<PrizeTier, 'odds'>[]
    tierMultiplier?: string
  }
  name: string
}

export type PrizeTierTabsProps = Pick<
  TierTableProps,
  'addOns' | 'licensedTerritory' | 'lotteryId'
> & {
  tabs: PrizeTierTab[]
}

const DEFAULT_TAB_NAME = 'DEFAULT'

const { lotteryId } = defineProps<PrizeTierTabsProps>()

const { t } = useI18n()

const { isNumberLottery, isPickLottery, lotteryType } = useLotteryType(lotteryId)

const getGroupedPrizeTiers = (prizeTiers: Omit<PrizeTier, 'odds'>[]) => [
  {
    prizeTiers: prizeTiers.map<TierTablePrizeTier>((prizeTier) => ({
      ...prizeTier,
      prize: prizeTier.amount,
    })),
  },
]

const getTranslatedAddOnTitle = (name?: Maybe<string>) => {
  if (!name) return null

  const addOnTranslation = t(`lottery.${lotteryId}.${name}`)

  if (isNumberLottery.value) return addOnTranslation
  if (isPickLottery.value) return t('info.prizes.tiertab.addon', { addOn: addOnTranslation })

  return null
}
</script>

<style lang="scss" scoped>
/* stylelint-disable-next-line selector-class-pattern */
:deep(.lc-tabs__tab) {
  height: 2.375rem;
  padding: token('size/space/gap/none');
}

.text-xs {
  font-size: 0.5625rem;
  line-height: 1.28;
}
</style>
